import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Brain, HeartPulse, CloudRain } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { IMAGES } from "../lib/content";

const DOPAMINE = [
  { label: "Baseline", natural: 100, meth: 100 },
  { label: "Natural reward", natural: 150, meth: 100 },
  { label: "On meth", natural: 100, meth: 1250 },
  { label: "The crash", natural: 95, meth: 55 },
  { label: "Early recovery", natural: 100, meth: 75 },
  { label: "12+ months free", natural: 100, meth: 90 },
];

const TABS = {
  brain: {
    icon: Brain,
    title: "The Brain",
    intro: "Methamphetamine releases dopamine far beyond anything natural rewards produce — which is why it hooks so fast, and why ordinary pleasures can feel flat for a while afterwards. The hopeful part: brain imaging studies show the dopamine system can partly recover with sustained abstinence. The brain heals.",
    points: [
      ["Reward flood", "Studies find meth pushes dopamine to many times natural levels — the core of its grip"],
      ["The dip", "After use, dopamine drops below normal — driving low mood, exhaustion and craving"],
      ["Healing", "Imaging research shows measurable brain recovery after a year or more of abstinence"],
    ],
  },
  body: {
    icon: HeartPulse,
    title: "The Body",
    intro: "Meth is a powerful stimulant that pushes every system hard — heart, skin, teeth, immune system — while appetite and sleep switch off for days at a time. The strain is real, and so is the body's capacity to recover with support.",
    points: [
      ["Heart", "Raised blood pressure and heart rate; research links heavy use to stroke and heart strain"],
      ["Visible toll", "Weight change, skin problems and dental decay are common with sustained use"],
      ["No rest", "Days without proper sleep or food weaken immunity and cloud judgement"],
    ],
  },
  mind: {
    icon: CloudRain,
    title: "The Mind",
    intro: "Anxiety, paranoia, low mood and, with heavy use, psychosis are documented effects of methamphetamine. These are chemical effects — not character flaws — and they lift with time, support and treatment.",
    points: [
      ["Psychosis", "Heavy use can bring paranoia and hallucinations that may persist for weeks or months"],
      ["The loop", "Comedown anxiety and depression can drive the next use — that's the cycle tightening"],
      ["Recovery works", "With support and treatment, mood, memory and clarity return — evidence backs this"],
    ],
  },
};

const SOURCES = [
  ["NIDA — Methamphetamine DrugFacts", "https://nida.nih.gov/publications/drugfacts/methamphetamine"],
  ["NZ Drug Foundation — Methamphetamine", "https://drugfoundation.org.nz/info/drug-index/methamphetamine/"],
  ["MSD — Social Connectedness & Wellbeing (2018)", "https://www.msd.govt.nz/about-msd-and-our-work/publications-resources/literature-reviews/social-connectedness-and-wellbeing/"],
];

export default function HarmMatrix() {
  const [tab, setTab] = useState("brain");
  const Active = TABS[tab];
  return (
    <section id="truth" className="bg-[#090A0F] py-24 sm:py-32 relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mono-label text-[#EF4444] mb-4">The facts, with sources</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight mb-4">
          What meth does to the<br />brain, body &amp; mind
        </motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-slate-400 max-w-2xl mb-10">
          Grounded in published research — shared so whānau and communities can make informed choices, and so support can start early. Every claim below links to its sources at the end of this section.
        </motion.p>

        <div className="flex gap-2 mb-10 flex-wrap" role="tablist">
          {Object.entries(TABS).map(([key, t]) => (
            <button
              key={key}
              data-testid={`harm-tab-${key}`}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${tab === key ? "bg-[#EF4444] text-white" : "border border-[#212636] text-slate-400 hover:text-white hover:border-slate-500"}`}
            >
              <t.icon size={16} /> {t.title}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="text-slate-200 text-lg leading-relaxed mb-8">{Active.intro}</p>
            <div className="space-y-4">
              {Active.points.map(([stat, desc]) => (
                <div key={stat} className="flex gap-5 border-l-2 border-[#EF4444] pl-5">
                  <span className="font-display font-extrabold text-[#EF4444] text-xl whitespace-nowrap min-w-[5.5rem]">{stat}</span>
                  <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div key={`viz-${tab}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
            {tab === "brain" ? (
              <div data-testid="dopamine-level-chart" className="bg-[#12141C] border border-[#212636] rounded-2xl p-6">
                <p className="mono-label text-slate-400 mb-4">Dopamine · % of natural baseline (illustrative)</p>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={DOPAMINE} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#212636" strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#12141C", border: "1px solid #212636", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#F8FAFC" }} />
                      <Area type="monotone" dataKey="meth" name="Meth" stroke="#EF4444" fill="#EF4444" fillOpacity={0.25} strokeWidth={2} />
                      <Area type="monotone" dataKey="natural" name="Natural" stroke="#10B981" fill="#10B981" fillOpacity={0.12} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-slate-500 mt-4">Illustrative, based on published research directions — not patient data. The dip below baseline helps explain craving; the recovery line is why we say hope is real.</p>
              </div>
            ) : (
              <div className="clipped-frame overflow-hidden">
                <img src={IMAGES.brain} alt="Neural pathways — research imagery" className="w-full h-80 object-cover opacity-80" />
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#212636]">
          <p className="mono-label text-slate-500 mb-3">Sources &amp; further reading</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {SOURCES.map(([label, url]) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-white underline underline-offset-4 transition-colors" data-testid={`source-${label.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
